/* eslint-disable */

import { ChartOptions, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useMemo, useRef, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';

import { htmlLegendPlugin } from 'common/utils/chart';
import KioskChartLegends from 'extends/KioskChartLegends';
import { useTranslation } from 'react-i18next';
import { formatNumberWithSeparators } from 'common/utils/functions';

interface AverageYearOldProps {
  averageYearOld?: {
    less40: number;
    between40And50: number;
    between50And69: number;
    over70: number;
  };
}

const KioskAverageYearOld: React.FC<AverageYearOldProps> = ({ averageYearOld }) => {
  const { t } = useTranslation()

  type AgeKeys = keyof typeof averageYearOld;

  const labelMap: Record<AgeKeys, string> = {
    less40: t('dashboard.less40'),
    between40And50: t('dashboard.between40and50'),
    between50And69: t('dashboard.between50and69'),
    over70: t('dashboard.over70')
  };

  const useMemoAverageData = useMemo(() => {
    if (!averageYearOld) {
      return {
        data: [],
        ageRange: []
      };
    }
    const averageYearOldData = [
      averageYearOld.over70,
      averageYearOld.between50And69,
      averageYearOld.between40And50,
      averageYearOld.less40
    ];
    const total = averageYearOldData.reduce((acc, curr) => acc + curr, 0);
    return {
      data: averageYearOldData,
      ageRange: [
        t('dashboard.over70'),
        t('dashboard.between50and69'),
        t('dashboard.between40and50'),
        t('dashboard.less40')
      ],
      percentage: averageYearOldData.map((item) => ((item / total) * 100).toFixed(2)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [averageYearOld]);

  const data = {
    datasets: [{
      data: useMemoAverageData.data,
      backgroundColor: ['#6c234a', '#9f336c', '#d274a6', '#e6b2ce'],
      borderWidth: 0
    }],
    labels: useMemoAverageData.ageRange,
  };

  const chartRef = useRef<any>(null);
  const [hiddens, setHiddens] = useState<boolean[]>(data.labels.map(() => false));

  const options: ChartOptions<'doughnut'> = useMemo(() => ({
    responsive: true,
    cutout: '0%',
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: '#fff',
        font: {
          size: 16,
          weight: 600
        },
        formatter: (value: number, context) => {
          if (value === 0) return '';
          const percentage = Math.round(Number(useMemoAverageData.percentage?.[context.dataIndex]));
          return [percentage > 2 ? `${percentage}%` : ''];
        }
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'doughnut'>) => {
            const percentage = useMemoAverageData.percentage?.[tooltipItem.dataIndex];
            return `${tooltipItem.label}: ${formatNumberWithSeparators(Number(tooltipItem.raw))} (${Math.round(Number(percentage))}%)`;
          },
        },
      },
      // htmlLegend: {
      //   containerID: 'legend-container-average',
      // },
    }
  }), [useMemoAverageData]);

  const handleLegendClick = (index: number) => {
    const chart = chartRef.current;

    if (!chart) return;

    // Toggle visibility
    chart.toggleDataVisibility(index);
    chart.update();

    // Update local state (to dim legend item)
    setHiddens((prev) => {
      const newHidden = [...prev];
      newHidden[index] = !newHidden[index];
      return newHidden;
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px',
        border: '1px solid #edeef1',
        borderRadius: '12px',
        height: '100%'
      }}
    >
      <h1
        style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#3D424F',
          marginBottom: '24px',
          width: '100%'
        }}
      >
        {t('dashboard2025.ageGroup')}
      </h1>
      <div style={{ width: '200px' }}>
        <Doughnut
          ref={chartRef}
          data={data}
          options={options}
          plugins={[ChartDataLabels, htmlLegendPlugin()]}
        />
      </div>
      <KioskChartLegends
        hiddens={hiddens}
        style={{
          marginTop: 20,
        }}
        labels={data.labels as string[]}
        colors={data.datasets[0].backgroundColor as string[]}
        onClick={handleLegendClick}
      />
    </div>
  );
};

export default KioskAverageYearOld;
